import type { DataSource, SiteRagReport } from '../../types/siteRag';

export const LEGACY_SIMULATION_REGEN_REASON = 'legacy-simulation-reference-retired' as const;
export const LEGACY_SIMULATION_REFERENCE_LABEL = 'legacy simulation reference retired' as const;
export const LEGACY_SIMULATION_REGEN_GUIDANCE =
  'Regenerate this report from validated simulation and neutral finance evidence.' as const;
const RETIRED_EVIDENCE_PREFIXES = ['bopSim:', 'bopAnnual:', 'bopLcoe:'] as const;
const RETIRED_DOCUMENT_URL_PREFIX = 'doc://sim/';

function containsRetiredEvidence(value: string): boolean {
  return RETIRED_EVIDENCE_PREFIXES.some((prefix) => value.includes(prefix))
    || value.includes(RETIRED_DOCUMENT_URL_PREFIX);
}

/** True only for historic execution/finance references; `eoSim:` remains valid EO evidence. */
export function isRetiredSimulationDataSource(
  reference: Pick<DataSource, 'field' | 'source' | 'url'> | null | undefined,
): boolean {
  if (!reference) return false;
  return [reference.field, reference.source, reference.url]
    .some((value) => typeof value === 'string' && containsRetiredEvidence(value));
}

/**
 * Nesting deeper than this is not report prose; it is a cycle or a foreign
 * object graph. `JSON.stringify` would have thrown on a cycle and the previous
 * scan returned false for it, so the walk keeps that answer.
 */
const RETIRED_SCAN_MAX_DEPTH = 32;

/**
 * Visit every string leaf of a report the way `JSON.stringify` would have
 * reached it (own enumerable properties, arrays in order), but without
 * materialising a multi-megabyte string per body and without the four
 * follow-up substring passes over it. Stops at the first hit.
 */
function containsRetiredEvidenceDeep(value: unknown, depth: number, seen: Set<object>): boolean {
  if (typeof value === 'string') return containsRetiredEvidence(value);
  if (value === null || typeof value !== 'object' || depth > RETIRED_SCAN_MAX_DEPTH) return false;
  if (seen.has(value)) return false;
  seen.add(value);
  if (Array.isArray(value)) {
    for (const entry of value) {
      if (containsRetiredEvidenceDeep(entry, depth + 1, seen)) return true;
    }
    return false;
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (containsRetiredEvidenceDeep(record[key], depth + 1, seen)) return true;
  }
  return false;
}

/**
 * The evidence-identity fields come first because that is where a retired
 * `bopSim:` / `bopAnnual:` / `bopLcoe:` identity is normally declared: the
 * report's `references[]` rows and each claim's compact `sources[]` tokens.
 * Historic narrative also embedded `doc://sim/…` links inline, and the eleven
 * report types carry prose on different typed fields, so the leaf walk stays
 * as the last resort rather than an allow-list that could miss one of them.
 */
function scanRetiredSimulationReference(report: SiteRagReport): boolean {
  if (report.references?.some(isRetiredSimulationDataSource)) return true;
  if (Array.isArray(report.claims)) {
    for (const claim of report.claims) {
      const sources = claim?.sources;
      if (Array.isArray(sources)
        && sources.some((source) => typeof source === 'string' && containsRetiredEvidence(source))) {
        return true;
      }
    }
  }
  return containsRetiredEvidenceDeep(report, 0, new Set());
}

/**
 * Scan verdicts keyed by the immutable artifact identity. A report list pass
 * re-presents the same bodies on every job completion and the reader memoises
 * on the body object; neither needs a second walk over an artifact whose
 * `sourceHash` / revision / storage pointer has not moved.
 */
const RETIREMENT_SCAN_CACHE_LIMIT = 1024;
const retirementScanCache = new Map<string, boolean>();

function identityString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Null when the verdict must not be cached: a metadata-only row shares its
 * identity with the full body that arrives later and would otherwise pin a
 * false negative onto it, and an unsaved draft has no immutable identity yet.
 * `updatedAt` rides along so a mutable legacy object that was edited in place
 * is rescanned after its next persist.
 */
function retirementScanCacheKey(report: SiteRagReport): string | null {
  if (report._metadataOnly === true) return null;
  const record = report as unknown as Record<string, unknown>;
  const id = identityString(record.id);
  if (!id) return null;
  const sourceHash = identityString(record.sourceHash);
  const reportRevisionId = identityString(record.reportRevisionId);
  const storagePath = identityString(record.storagePath);
  if (!sourceHash && !reportRevisionId && !storagePath) return null;
  return `${id}|${sourceHash}|${reportRevisionId}|${storagePath}|${identityString(record.updatedAt)}`;
}

function rememberRetirementVerdict(key: string, verdict: boolean): void {
  if (retirementScanCache.size >= RETIREMENT_SCAN_CACHE_LIMIT) {
    const oldest = retirementScanCache.keys().next();
    if (!oldest.done) retirementScanCache.delete(oldest.value);
  }
  retirementScanCache.set(key, verdict);
}

/** Test hook — forget cached scan verdicts. */
export function clearRetiredSimulationReferenceCacheForTests(): void {
  retirementScanCache.clear();
}

/** Historic BoP identities must never be silently interpreted as EO evidence. */
export function hasRetiredSimulationReference(report: SiteRagReport): boolean {
  if (report.requiresRegeneration?.reason === LEGACY_SIMULATION_REGEN_REASON) return true;
  const key = retirementScanCacheKey(report);
  if (key !== null) {
    const cached = retirementScanCache.get(key);
    if (cached !== undefined) return cached;
  }
  const verdict = scanRetiredSimulationReference(report);
  if (key !== null) rememberRetirementVerdict(key, verdict);
  return verdict;
}

export class RetiredSimulationReferenceError extends Error {
  override readonly name = 'RetiredSimulationReferenceError';

  constructor(format: string) {
    super(
      `Cannot export this ${format}: it contains a retired legacy simulation reference. `
      + LEGACY_SIMULATION_REGEN_GUIDANCE,
    );
  }
}

/** Idempotent migration marker. Content is left untouched for auditability and must be regenerated. */
export function markRetiredSimulationReferences(report: SiteRagReport): SiteRagReport {
  if (!hasRetiredSimulationReference(report)) return report;
  if (report.requiresRegeneration?.reason === LEGACY_SIMULATION_REGEN_REASON) return report;
  return {
    ...report,
    requiresRegeneration: {
      reason: LEGACY_SIMULATION_REGEN_REASON,
      detectedAt: new Date().toISOString(),
    },
  };
}

/** Delivery artifacts cannot carry retired execution evidence or imply EO equivalence. */
export function assertNoRetiredSimulationReferences(
  report: SiteRagReport,
  format = 'report',
): void {
  if (!hasRetiredSimulationReference(report)) return;
  throw new RetiredSimulationReferenceError(format);
}
